import * as React from "react";
import * as Sentry from "@sentry/react";
import { Check, Copy } from "lucide-react";
import { toast } from "sonner";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";

const COPIED_RESET_MS = 2000;

interface CopyButtonProps
  extends Omit<React.ComponentProps<"button">, "value" | "onClick"> {
  /** The exact text placed on the clipboard. */
  value: string;
  /**
   * Noun describing the value, used for the tooltip and accessible name, e.g.
   * "serial number" renders as "Copy serial number".
   */
  label: string;
}

/**
 * Small copy-to-clipboard icon button.
 *
 * Keeps clicks and Enter/Space presses to itself, so it can sit safely inside
 * clickable table rows and next to links without triggering navigation.
 */
function CopyButton({
  value,
  label,
  className,
  onKeyDown,
  ...props
}: CopyButtonProps) {
  const [copied, setCopied] = React.useState(false);
  const resetTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  React.useEffect(
    () => () => {
      if (resetTimeoutRef.current) {
        clearTimeout(resetTimeoutRef.current);
      }
    },
    []
  );

  const handleCopy = React.useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      // Keep the click away from clickable rows and wrapping links.
      event.preventDefault();
      event.stopPropagation();

      try {
        if (!navigator.clipboard?.writeText) {
          throw new Error("Clipboard API unavailable");
        }

        await navigator.clipboard.writeText(value);

        setCopied(true);
        if (resetTimeoutRef.current) {
          clearTimeout(resetTimeoutRef.current);
        }
        resetTimeoutRef.current = setTimeout(() => {
          setCopied(false);
        }, COPIED_RESET_MS);
      } catch (error) {
        Sentry.captureException(error);
        toast.error(`Failed to copy ${label}`);
      }
    },
    [label, value]
  );

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      // Clickable rows (the cassette list, the users list) handle Enter and
      // Space on keydown and call preventDefault, which would both suppress
      // this button's own activation and trigger the row. Keep them to
      // ourselves so keyboard users can copy.
      if (event.key === "Enter" || event.key === " ") {
        event.stopPropagation();
      }

      onKeyDown?.(event);
    },
    [onKeyDown]
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={copied ? `Copied ${label}` : `Copy ${label}`}
          className={cn(
            "inline-flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            className
          )}
          onClick={handleCopy}
          onKeyDown={handleKeyDown}
          {...props}
        >
          {copied ? (
            <Check className="size-3.5 text-emerald-500" aria-hidden="true" />
          ) : (
            <Copy className="size-3.5" aria-hidden="true" />
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent>{copied ? "Copied!" : `Copy ${label}`}</TooltipContent>
    </Tooltip>
  );
}

export { CopyButton };
